import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ArrowRight } from "lucide-react";
import { navigation, companyInfo } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
    setMobileDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=90,format=auto/assets/b6ab12dc-9087-41bb-8b7f-45b54510818a/screenshot-2026-06-08-at-11-50-13-removebg-preview.png"
            alt="Godske Byg & Anlæg"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {navigation.links.map((link) => {
            if (link.dropdown) {
              return (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    onMouseEnter={() => setDropdownOpen(true)}
                    className={`flex items-center gap-1 text-sm font-semibold tracking-wider uppercase transition-colors hover:text-brand-charcoal py-2 ${
                      isActive(link.path) ? "text-brand-charcoal font-bold" : "text-gray-500"
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropdownOpen && (
                    <div
                      onMouseLeave={() => setDropdownOpen(false)}
                      className="absolute left-0 mt-1 w-56 rounded-md border border-gray-100 bg-white py-2 shadow-xl"
                    >
                      {link.dropdown.map((subLink) => (
                        <Link
                          key={subLink.label}
                          to={subLink.path}
                          className={`block px-4 py-2.5 text-sm font-semibold tracking-wider uppercase transition-colors hover:bg-gray-50 hover:text-brand-charcoal ${
                            location.pathname === subLink.path ? "text-brand-charcoal bg-gray-50 font-bold" : "text-gray-500"
                          }`}
                        >
                          {subLink.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={link.label}
                to={link.path}
                className={`text-sm font-semibold tracking-wider uppercase transition-colors hover:text-brand-charcoal py-2 ${
                  isActive(link.path) ? "text-brand-charcoal font-bold" : "text-gray-500"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA Button (>= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-6">
          <a
            href={`tel:${companyInfo.phone}`}
            className="flex items-center gap-2 text-sm font-semibold text-brand-charcoal hover:text-brand-accent transition-colors"
          >
            <Phone className="h-4 w-4 text-brand-charcoal" />
            <span className="whitespace-nowrap">{companyInfo.phoneFormatted}</span>
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded bg-brand-accent px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-accent-hover hover:scale-[1.02] active:scale-[0.98]"
          >
            Få et tilbud
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Hamburger Button (< 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex p-2 text-brand-charcoal hover:text-brand-accent min-[1200px]:hidden focus:outline-none"
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {isOpen && (
        <div className="border-t border-gray-100 bg-white min-[1200px]:hidden shadow-lg">
          <div className="space-y-1 px-4 py-4 sm:px-6">
            {navigation.links.map((link) => {
              if (link.dropdown) {
                return (
                  <div key={link.label} className="space-y-1 py-1">
                    <button
                      onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                      className="flex w-full items-center justify-between px-3 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:text-brand-charcoal rounded transition-colors"
                    >
                      <span className="uppercase tracking-wider">{link.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {mobileDropdownOpen && (
                      <div className="pl-4 border-l border-gray-100 ml-3 space-y-1 mt-1">
                        {link.dropdown.map((subLink) => (
                          <Link
                            key={subLink.label}
                            to={subLink.path}
                            className={`block rounded px-3 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                              location.pathname === subLink.path
                                ? "text-brand-charcoal bg-gray-100 font-bold"
                                : "text-gray-500 hover:bg-gray-50 hover:text-brand-charcoal"
                            }`}
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`block rounded px-3 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive(link.path)
                      ? "text-brand-charcoal bg-gray-100 font-bold"
                      : "text-gray-500 hover:bg-gray-50 hover:text-brand-charcoal"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}

            <div className="border-t border-gray-100 pt-4 mt-4 space-y-3 px-3">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 text-sm font-semibold text-brand-charcoal hover:text-brand-accent transition-colors"
              >
                <Phone className="h-4 w-4 text-brand-accent" />
                <span>{companyInfo.phoneFormatted}</span>
              </a>
              <Link
                to="/kontakt"
                className="flex w-full items-center justify-center gap-2 rounded bg-brand-accent py-3 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-accent-hover"
              >
                Få et tilbud
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
